import { Wordmark } from "@/app/logo"
import { DemoLink } from "@/components/demo_link"
import { classes } from "@/utils"
import { StarIcon } from "@heroicons/react/20/solid"
import { LinkedinIcon, MinusCircle, PlayIcon, PlusCircle, SparklesIcon, TwitterIcon } from "lucide-react"
import { ComponentChildren } from "preact"
import { useEffect, useState } from "preact/hooks"

export { DEMO_LINK } from "@/components/demo_link"

/**
 * Product recordings shown across the landing page.
 * Each entry is null until the recording exists — drop the file in
 * app/public/img/landing/media/ and set the path here (mp4/webm loops or GIFs).
 * The `note` describes exactly what to record.
 * Not every entry is wired to a card: cashapp/tpm/forecasting/sales are staged for later, and
 * hero is unused (the platform-tour clip plays directly in the Benefits section). To show one,
 * record the clip, set `src`, then attach it to a module via its `media` field.
 */
const MEDIA: Record<string, { src: string | null; label: string; note: string }> = {
  hero: {
    src: null, // e.g. "/img/landing/media/hero.mp4"
    label: "Platform tour",
    note: "30-45s silent loop: deduction lands → AI validates it with backup docs → one-click dispute → repayment matched → dashboard totals tick up.",
  },
  deductions: {
    src: "/img/landing/media/deductions.mp4",
    label: "Deductions in action",
    note: "10-15s loop: deductions table auto-categorizing, opening a deduction to show matched backup docs, clicking Dispute.",
  },
  wizard: {
    src: "/img/landing/media/wizard.mp4",
    label: "Meet the Wizard",
    note: "10-15s loop: asking the Wizard 'what happened with this chargeback?', it pulls the deduction, explains it, and files the dispute.",
  },
  cashapp: {
    src: null, // e.g. "/img/landing/media/cashapp.mp4"
    label: "Cash application",
    note: "10-15s loop: opening a check, auto-matching it across invoices and deductions, syncing to QuickBooks.",
  },
  tpm: {
    src: null, // e.g. "/img/landing/media/tpm.mp4"
    label: "Trade promotions",
    note: "10-15s loop: trade promotion calendar/spend view, drilling into a promo to compare planned vs. actual spend.",
  },
  forecasting: {
    src: null, // e.g. "/img/landing/media/forecasting.mp4"
    label: "Sales forecasting",
    note: "10-15s loop: forecast chart by customer/SKU, adjusting an assumption and watching the forecast update.",
  },
  sales: {
    src: null, // e.g. "/img/landing/media/sales.mp4"
    label: "Sales analysis",
    note: "10-15s loop: sales dashboard filtering by retailer/period, velocity trends updating live.",
  },
}

interface MediaSlotProps {
  media: { src: string | null; label: string; note: string }
  className?: string
}

/** Renders a product recording when available, otherwise a branded placeholder frame. */
function MediaSlot({ media, className }: MediaSlotProps) {
  if (media.src) {
    if (media.src.endsWith(".gif") || media.src.endsWith(".png") || media.src.endsWith(".webp")) {
      return <img src={media.src} alt={media.label} class={classes("rounded-2xl w-full object-cover", className)} />
    }
    return (
      <video
        class={classes("rounded-2xl w-full object-cover", className)}
        src={media.src}
        autoplay
        muted
        loop
        playsinline
      />
    )
  }
  return (
    <div
      class={classes(
        "rounded-2xl w-full bg-white/70 border-2 border-dashed border-indigo-300 flex flex-col items-center justify-center text-center p-6 min-h-48",
        className
      )}>
      <div class="bg-indigo rounded-full p-3">
        <PlayIcon className="w-6 h-6 text-white" />
      </div>
      <div class="font-display text-xl mt-3 text-indigo-900">{media.label}</div>
      <div class="text-xs text-gray-500 mt-2 max-w-sm">Recording to add: {media.note}</div>
    </div>
  )
}

export function Landing() {
  let year = new Date().getFullYear()

  return (
    <div class="flex flex-col min-h-full w-full">
      <div class="landing-container font-content">
        <Hero2 />
        <Platform />
        <Benefits />
        <Testimonials />
        <Pricing />
        <Team />
        <FinalCta />
        <SocialFooter />
        <div className="bg-orange-100 pb-8 text-center text-gray-600 text-sm">
          <div className="max-w-7xl mx-auto px-4">
            <GoogleDataNotice />
            <p>© {year} MarginWiz. All rights reserved.</p>
            <p className="mt-2">
              <a href="/privacy.html" className="text-indigo-600 hover:underline">
                Privacy Policy
              </a>
              {" | "}
              <a href="/terms.html" className="text-indigo-600 hover:underline">
                Terms of Service
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Says on the homepage what MarginWiz does with a connected Google account. Google's OAuth
 * verification rejects an app whose homepage doesn't explain its purpose and name the app its
 * consent screen shows; keep this in step with the "Google user data" section of /privacy.html.
 */
function GoogleDataNotice() {
  return (
    <p className="max-w-3xl mx-auto mb-4">
      <strong>How MarginWiz uses Google data:</strong> if you connect Gmail or Google Drive, MarginWiz reads the
      remittances, deduction notices and backup documents in it to match them to your deductions, and sends only the
      dispute emails you ask it to. We never use this data for advertising or to train general AI models. See our{" "}
      <a href="/privacy.html#google_user_data" className="text-indigo-600 hover:underline">
        Privacy Policy
      </a>
      .
    </p>
  )
}

interface SocialFooterProps {
  /** Override bar background (default matches landing + blog: `bg-orange-100`). */
  className?: string
}

export function SocialFooter(props: SocialFooterProps = {}) {
  const { className } = props
  return (
    <div class={classes("flex justify-center space-x-6 py-6", className ?? "bg-orange-100")}>
      <a
        href="https://www.linkedin.com/company/marginwiz"
        target="_blank"
        rel="noopener noreferrer"
        class="text-indigo-700 transition-colors hover:text-indigo-950">
        <LinkedinIcon className="w-6 h-6" />
      </a>
      <a
        href="https://x.com/MarginWiz"
        target="_blank"
        rel="noopener noreferrer"
        class="text-indigo-700 transition-colors hover:text-indigo-950">
        <TwitterIcon className="w-6 h-6" />
      </a>
      <a href="/blog" class="font-display flex items-center space-x-2 text-indigo-700 transition-colors hover:text-indigo-950">
        <span class="text-lg font-semibold">Blog</span>
      </a>
    </div>
  )
}

/** Small uppercase label above a section title — one style everywhere so sections read as a system. */
function SectionEyebrow({ children, className }: { children: ComponentChildren; className?: string }) {
  return <div class={classes("uppercase text-center text-sm font-bold tracking-wider", className)}>{children}</div>
}

/** Section heading with a consistent size ramp and gap below the eyebrow. */
function SectionTitle({ children, className }: { children: ComponentChildren; className?: string }) {
  return <div class={classes("font-display text-4xl lg:text-5xl text-center mt-4", className)}>{children}</div>
}

const NAV_LINKS = [
  { href: "#platform", label: "Platform" },
  { href: "#integrations", label: "Integrations" },
  { href: "#pricing", label: "Pricing" },
  { href: "#team", label: "Team" },
  { href: "/blog", label: "Blog" },
]

export function HeaderBar() {
  return (
    <div class="flex flex-wrap-reverse justify-between items-center">
      <Wordmark />
      <div class="hidden lg:flex items-center space-x-6">
        {NAV_LINKS.map(link => (
          <a key={link.href} class="text-gray-900 font-semibold hover:text-indigo-700 transition-colors" href={link.href}>
            {link.label}
          </a>
        ))}
      </div>
      <div class="space-x-4 hidden lg:flex items-center">
        <a
          class="px-4 py-2 text-gray-900 border border-indigo rounded-full uppercase text-sm font-bold hover:bg-white/60 transition-colors"
          href="/login">
          Login
        </a>
        <DemoLink class="px-4 py-2 border border-indigo bg-indigo rounded-full text-white uppercase text-sm font-bold hover:bg-indigo-600 hover:border-indigo-600 transition-colors" />
      </div>
    </div>
  )
}

export function HeroInner() {
  return (
    <div class="flex flex-wrap justify-between items-center">
      <div class="mt-12 lg:mt-24 lg:max-w-xl">
        <div class="inline-flex items-center gap-2 bg-white/80 border border-indigo-200 rounded-full px-4 py-1.5 text-sm font-bold uppercase tracking-wide text-indigo-700">
          <SparklesIcon className="w-4 h-4" />
          The AI operating system for CPG
        </div>
        <h1 class="font-display text-5xl lg:text-6xl mt-6">
          You won the shelf. <br /> Now win the margin.
        </h1>
        <div class="text-lg lg:text-xl mt-6 max-w-lg">
          MarginWiz's AI agents know how to make sure you keep every dollar you earned.
        </div>
        <div class="flex items-center mt-8 space-x-4">
          <DemoLink class="px-5 py-2.5 border border-indigo bg-indigo rounded-full text-white uppercase text-sm font-bold hover:bg-indigo-600 hover:border-indigo-600 transition-colors" />
          <a
            class="px-5 py-2.5 text-gray-900 border border-indigo rounded-full uppercase text-sm font-bold hover:bg-white/60 transition-colors"
            href="mailto:sales@marginwiz.com">
            Contact Us
          </a>
        </div>
      </div>
      <img class="w-56 lg:w-80 mt-12 lg:mt-16 mx-auto lg:mx-0" src="/img/landing/soda.webp" alt="" />
    </div>
  )
}

const RETAIL_LOGOS = [
  { src: "/img/integration/kehe.webp", class: "h-11" },
  { src: "/img/integration/unfi.webp", class: "h-9" },
  { src: "/img/integration/target.webp", class: "h-9" },
  { src: "/img/integration/vitaminshoppe.webp", class: "h-10" },
  { src: "/img/integration/amazon.webp", class: "h-9 mt-2" },
  { src: "/img/integration/walmart.webp", class: "h-9" },
  { src: "/img/integration/kroger.webp", class: "h-9" },
  { src: "/img/integration/ahold.webp", class: "h-9 -mt-2" },
  { src: "/img/integration/wakefern.webp", class: "h-9" },
  { src: "/img/integration/cands.webp", class: "h-9" },
  { src: "/img/integration/bozzutos.webp", class: "h-9" },
  { src: "/img/integration/sps.webp", class: "h-9" },
  { src: "/img/integration/rainforest.webp", class: "h-9" },
]

// Logistics providers (3PLs, freight & DSD). Add more by dropping a logo in
// app/public/img/integration/, running `just optimize-images`, and listing it here.
const LOGISTICS_LOGOS = [
  { src: "/img/integration/stord.webp", class: "h-9" },
  { src: "/img/integration/chrobinson.webp", class: "h-8" },
  // Freightzy is a partner (BOL/POD sync in progress), so its logo links to them. SVG from their
  // own site header (freightzy.com).
  { src: "/img/integration/freightzy.svg", class: "h-10", href: "https://www.freightzy.com/", alt: "Freightzy" },
  { src: "/img/integration/tql.webp", class: "h-10" },
  { src: "/img/integration/echo.webp", class: "h-8" },
  { src: "/img/integration/rjw.webp", class: "h-8" },
]

const ACCOUNTING_LOGOS = [
  { src: "/img/integration/quickbooks.webp", class: "h-9" },
  { src: "/img/integration/xero.webp", class: "h-9" },
  { src: "/img/integration/sageintacct.webp", class: "h-9" },
  { src: "/img/integration/netsuite_logo.webp", class: "h-8" },
]

interface LogoEntry {
  src: string
  class: string
  /** A partner's site; the logo opens it in a new tab. */
  href?: string
  alt?: string
}

/** A titled row of masked partner logos inside the connections card. */
function LogoGroup({ title, logos, showMore }: { title: string; logos: LogoEntry[]; showMore?: boolean }) {
  return (
    <div class="flex flex-col items-center py-6 first:pt-0 last:pb-0">
      <div class="uppercase text-xs font-bold tracking-wider text-black/50">{title}</div>
      <div class="flex flex-wrap justify-center items-center gap-x-10 gap-y-5 mt-4">
        {logos.map((logo: LogoEntry) => {
          const img = <img key={logo.src} src={logo.src} alt={logo.alt ?? ""} class={classes(logo.class, "img-masked")} />
          if (!logo.href) return img
          return (
            <a key={logo.src} href={logo.href} target="_blank" rel="noopener" title={logo.alt}>
              {img}
            </a>
          )
        })}
        {showMore && <div class="font-display text-2xl">& more!</div>}
      </div>
    </div>
  )
}

// MarginWiz customers shown as social proof above the integrations band. `logo` points to a
// full-color PNG under app/public/img/; brands without a logo file yet fall back to a styled
// wordmark. To upgrade one, drop the official logo in (e.g. app/public/img/brands/) and set `logo`.
const TRUSTED_BRANDS: { name: string; logo?: string }[] = [
  { name: "GoodPop", logo: "/img/brands/goodpop.svg" },
  { name: "Saffron Road", logo: "/img/brands/saffron-road.webp" },
  { name: "Mauna Loa", logo: "/img/brands/mauna-loa.webp" },
  { name: "De Soi", logo: "/img/brands/de-soi.webp" },
  { name: "Nutiva", logo: "/img/brands/nutiva.webp" },
  { name: "Super Coffee", logo: "/img/testimonials/super.webp" },
  { name: "Sanzo", logo: "/img/brands/sanzo.webp" },
  { name: "Ithaca Hummus", logo: "/img/brands/ithaca-hummus.webp" },
]

export function TrustedBy() {
  return (
    <div class="mt-16 lg:mt-20">
      <SectionEyebrow>Trusted by 50+ brands including</SectionEyebrow>
      <div class="flex flex-wrap justify-center items-center gap-x-8 gap-y-4 mt-4 rounded-2xl bg-white shadow-sm px-8 py-5 max-w-6xl mx-auto">
        {TRUSTED_BRANDS.map(brand =>
          brand.logo ? (
            <img key={brand.name} src={brand.logo} alt={brand.name} class="h-8 lg:h-9 object-contain" />
          ) : (
            <span key={brand.name} class="font-display text-lg lg:text-xl text-indigo-900/70 whitespace-nowrap">
              {brand.name}
            </span>
          )
        )}
      </div>
    </div>
  )
}

export function Integrations() {
  return (
    <div id="integrations" class="mt-24 pb-12">
      <SectionEyebrow>Connected to your entire supply chain</SectionEyebrow>
      <div class="mt-4 rounded-xl bg-salmon px-6 py-6 divide-y divide-black/10">
        <LogoGroup title="Retailers & Distributors" logos={RETAIL_LOGOS} showMore />
        <LogoGroup title="Logistics Providers" logos={LOGISTICS_LOGOS} showMore />
        <LogoGroup title="Accounting & ERP" logos={ACCOUNTING_LOGOS} showMore />
      </div>
    </div>
  )
}

export function Hero2() {
  return (
    <div
      class="pt-3 lg:pt-6 px-4 bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: 'url("/img/landing/hero2bg.webp")' }}>
      <HeaderBar />
      <HeroInner />
      <TrustedBy />
      <Integrations />
    </div>
  )
}

interface Module {
  eyebrow: string
  title: string
  description: string
  color: string
  /** Product recording. Omit for a text-only card — we keep videos to a couple of hero modules so the grid doesn't feel like a wall of loops. */
  media?: { src: string | null; label: string; note: string }
  wide?: boolean
}

// Feature cards for the platform grid, in visual order. Only the finance hero keeps a video here;
// the Wizard card (also with a video) is rendered separately in the bottom-right so it gets more room.
const MODULES: Module[] = [
  {
    eyebrow: "finance",
    title: "Discover. Dispute. Done.",
    description:
      "Every deduction from every retailer and distributor, tracked, validated against your promo calendar and backup docs then disputed as needed.",
    color: "bg-[#CEE4FF]",
    media: MEDIA.deductions,
    wide: true,
  },
  {
    eyebrow: "accounting",
    title: "Cash application, in a few clicks.",
    description:
      "Every check matched across your invoices and deductions, credit memos created with the right GL coding, and payments applied — straight in QuickBooks, Xero, Sage Intacct, or NetSuite.",
    color: "bg-[#FFCBD7]",
  },
  {
    eyebrow: "sales",
    title: "Trade spend, without the guesswork.",
    description:
      "Plan and track every promotion in one place from contract to lift analysis. POS and distributor sales data unified, with velocity trends by customer and SKU.",
    color: "bg-[#BCFD6D]",
  },
  {
    eyebrow: "operations",
    title: "Forecasts you can actually trust.",
    description:
      "Demand and sales forecasts built from your live distributor and retailer data, so sales, ops, and finance are always working from the same numbers.",
    color: "bg-[#FDD100]",
  },
  {
    eyebrow: "leadership",
    title: "Clarify your trade rate drivers.",
    description:
      "Analyze and report on your deduction rate trends over time, with breakdowns by type.",
    color: "bg-[#CEE4FF]",
  },
]

// Rendered as a wide card in the bottom-right of the platform grid so the Wizard recording has room to breathe.
const WIZARD_MODULE: Module = {
  eyebrow: "your new ai team",
  title: "The Wizards never sleep.",
  description: "",
    // "Your new AI teammate that never sleeps and lives in your data. It matches promotions and deductions to analzye trade spend, files disputes, and delivers intelligence briefs while you sip your coffee.",
  // White so the card reads as a card — the old #FBEDE0 was near-identical to the section's bg-orange-100.
  color: "bg-white",
  media: MEDIA.wizard,
  wide: true,
}

/** A single platform-grid card: eyebrow, title, description, and an optional product recording. */
function ModuleCard({ module }: { module: Module }) {
  const has_media = !!module.media
  return (
    <div
      class={classes(
        module.color,
        "rounded-4xl p-8 flex flex-col",
        module.wide ? "lg:col-span-4" : "lg:col-span-2",
        // Text-only cards can stretch beside a tall video card; center their content so the extra height reads as intentional.
        !has_media && "justify-center"
      )}>
      <div class="uppercase text-xs font-bold tracking-wide">{module.eyebrow}</div>
      <div class="font-display text-3xl mt-2">{module.title}</div>
      {module.description && <div class={classes("mt-3", has_media && "grow")}>{module.description}</div>}
      {module.media && <MediaSlot media={module.media} className="mt-6" />}
    </div>
  )
}

export function Platform() {
  return (
    <div id="platform" class="bg-orange-100 pt-16 pb-8">
      <div class="max-w-6xl mx-auto px-4">
        <SectionEyebrow>Nutrition facts</SectionEyebrow>
        <SectionTitle className="max-w-3xl mx-auto">
          All the ingredients you need to control your growth and margin.
        </SectionTitle>
        {/* MarginWiz is the AI operating system for CPG brands. */}
        {/* For all the hardest parts of your business, there's a AI wizard for that. */}
        <div class="grid grid-cols-1 lg:grid-cols-6 gap-6 mt-12">
          {MODULES.map(module => (
            <ModuleCard key={module.title} module={module} />
          ))}
          <div class="bg-[#5F5FFA] rounded-4xl p-8 flex flex-col items-center justify-center text-center lg:col-span-2">
            <img class="w-32" src="/img/landing/mascot_money.webp" alt="" />
            <div class="font-display text-3xl text-white mt-4">It takes a wiz to grow your biz.</div>
            <DemoLink class="mt-6 px-5 py-2.5 bg-white rounded-full text-indigo-900 uppercase text-sm font-bold hover:bg-indigo-50 transition-colors" />
          </div>
          {/* Wizard card last so it lands in the bottom-right of the grid, wide for the recording. */}
          <ModuleCard module={WIZARD_MODULE} />
        </div>
      </div>
    </div>
  )
}

const WIZARDS = [
  {
    color: "bg-indigo",
    name: "The Wizard",
    blurb: "The original wizard that reviews each deduction to decide whether it's disputable — and when it is, writes the dispute message for you.",
  },
  {
    color: "bg-salmon",
    name: "BigWiz",
    blurb: "The most ambitious wiz of them all. Prefers to work late at night. Lives to get results and boost your bottom line.",
  },
  {
    color: "bg-[#269E5C]",
    name: "CodingWiz",
    blurb: "The accountant's best friend. Enjoys gymnastics and coding deductions with accurate promo start and end dates. And yes it can do splits.",
  },
  {
    color: "bg-[#FF7398]",
    name: "TradeWiz",
    blurb: "The social butterfly of the team. Can talk all day about how your trade promotions are performing — which types performed best, which retailers overspent plan.",
  },
  {
    color: "bg-[#5F5FFA]",
    name: "SalesWiz",
    blurb: "Nerdy but knows how to have fun. Answers questions about your sales from your live retailer and distributor data — is growth from distribution or velocity?",
  },
]

export function Benefits() {
  return (
    <div class="pt-16 bg-orange-100">
      <SectionEyebrow>Why brands choose us</SectionEyebrow>
      <SectionTitle>
        Time is money.
        <br /> Our Wizards save you both.
      </SectionTitle>
      {/* 6-col grid with cards spanning 2 so the 2-card last row can center under the 3-card first row. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-6 px-4 max-w-5xl mx-auto mt-12">
        {WIZARDS.map((wizard, index) => (
          <div class={classes("bg-white rounded-3xl p-6 flex flex-col lg:col-span-2", index === 3 && "lg:col-start-2")}>
            <div class="flex items-center gap-3">
              <div class={classes(wizard.color, "rounded-full p-2 shrink-0")}>
                <SparklesIcon className="w-4 h-4 text-white" />
              </div>
              <div class="font-display text-xl">{wizard.name}</div>
            </div>
            <div class="mt-3 text-gray-700">{wizard.blurb}</div>
          </div>
        ))}
      </div>
      <div class="flex flex-wrap items-end mt-8 lg:mt-20 gap-y-6 justify-center px-4">
        <img class="w-1/2 lg:w-[250px] lg:-mr-6 relative z-1 lg:mb-8" src="/img/landing/mascot_money.webp" alt="" />
        <video
          class="w-full lg:w-[853px] rounded-2xl shadow-xl"
          src="/img/landing/media/hero.mp4"
          autoplay
          muted
          loop
          playsinline
        />
      </div>
    </div>
  )
}

const TESTIMONIALS = [
  {
    text: "I see MarginWiz becoming the operating system for CPG brands. They're dialed in on the most important problem first though, which is deductions.",
    name: "Jim DeCicco",
    role: "Co-founder at Super Coffee",
    photo: "/img/testimonials/jim.webp",
    logo: "/img/testimonials/super.webp",
    alt: "Super Coffee",
  },
  {
    text: "I've been waiting the last 15 years of my career for something like MarginWiz. I needed this at Poppi, and I'm so glad they've made deductions easy to manage.",
    name: "Roxanne McBride",
    role: "CFO at Bellway",
    photo: "/img/testimonials/roxanne.webp",
    logo: "/img/testimonials/bellway.webp",
    alt: "Bellway",
  },
  {
    text: "What this gives me more than anything is peace of mind. With MarginWiz, now at least I know where my money is going and that we are doing our best to win back what is ours.",
    name: "Jon Reed",
    role: "CEO at Compostic",
    photo: "/img/testimonials/jon.webp",
    logo: "/img/testimonials/compostic.webp",
    alt: "Compostic",
  },
  {
    text: "Nat and the team care about their mission of helping brands so much, and I'm grateful to work with people whose passion matches mine.",
    name: "Sophia Maroon",
    role: "CEO at Dress It Up Dressing",
    photo: "/img/testimonials/sophia.webp",
    logo: "/img/testimonials/dressitup.webp",
    alt: "Dress It Up Dressing",
  },
  {
    text: "I used to dread doing deductions work, but now I actually enjoy how efficient I can be with MarginWiz and celebrating the repayments when they come through.",
    name: "Rachel Bower",
    role: "Sr. Director of Planning at Bellway",
    photo: "/img/testimonials/rachel.webp",
    logo: "/img/testimonials/bellway.webp",
    alt: "Bellway",
  },
  {
    text: "I was unsure that an upstart logo could handle the complexity of our deductions, but MarginWiz met our needs perfectly and it has been a game changer for us.",
    name: "Walter Gordon",
    role: "Global Vice President at Diageo",
    photo: "/img/testimonials/walter.webp",
    logo: "/img/testimonials/diageo.webp",
    alt: "Diageo",
  },
  {
    text: "When I first logged into the portal, it was love at first sight.",
    name: "Sarah Bell",
    role: "Co-founder of Spring & Mulberry",
    photo: "/img/testimonials/sarah_mul.webp",
    logo: "/img/testimonials/springandmulberry.webp",
    alt: "Spring & Mulberry",
  },
  {
    text: "I'm continuously blown away at how quickly the team improves the platform with new integrations and features. I feel so grateful to have found MarginWiz when I did.",
    name: "JP Mackey",
    role: "President at Scharffen Berger Chocolates",
    photo: "/img/testimonials/jp.webp",
    logo: "/img/testimonials/scharffen.webp",
    alt: "Scharffen Berger Chocolates",
  },
]

export function Testimonials() {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)

  useEffect(() => {
    const interval = setTimeout(() => {
      setIsTransitioning(true)
      setTimeout(() => {
        setCurrentIndex(prev => (prev + 1) % TESTIMONIALS.length)
        setIsTransitioning(false)
      }, 500)
    }, 5000) // Rotate every 5 seconds
    return () => clearInterval(interval)
  }, [currentIndex])

  function handlePhotoClick(index: number) {
    setIsTransitioning(true)
    setTimeout(() => {
      setCurrentIndex(index)
      setIsTransitioning(false)
    }, 500)
  }

  let longest_text = TESTIMONIALS.reduce((a, b) => (a.length > b.text.length ? a : b.text), TESTIMONIALS[0].text)
  let testimonial = TESTIMONIALS[currentIndex]
  return (
    <div className="bg-orange-100 py-16 px-4">
      <div className="bg-indigo-500 rounded-[50px] px-8 pt-16 pb-8 max-w-6xl mx-auto relative flex flex-col">
        {/* Testimonial Content */}
        <div class="flex mx-auto">
          <StarIcon class="h-6 text-lime-200" />
          <StarIcon class="h-6 text-lime-200" />
          <StarIcon class="h-6 text-lime-200" />
          <StarIcon class="h-6 text-lime-200" />
          <StarIcon class="h-6 text-lime-200" />
        </div>

        <div class={classes("transition-opacity duration-300", isTransitioning ? "opacity-0" : "opacity-100")}>
          <div class="text-white text-4xl text-center max-w-3xl mx-auto relative mt-9 font-display">
            <div class="absolute">{testimonial.text}</div>
            <div class="opacity-0">{longest_text}</div>
          </div>
          <div className="text-center text-white mt-4">
            <div className="text-xl font-display">{testimonial.name}</div>
            <div className="text-sm opacity-75">{testimonial.role}</div>
            <img src={testimonial.logo} alt={testimonial.alt} className="h-12 object-contain rounded-xl mx-auto mt-2" />
          </div>
        </div>

        {/* Photo Navigation */}
        <div class="flex justify-center gap-4 mt-12">
          {TESTIMONIALS.map((testimonial, index) => (
            <button
              key={index}
              onClick={() => handlePhotoClick(index)}
              className={classes(
                "transition-transform duration-300 rounded-md",
                currentIndex === index ? "scale-110 ring-4 ring-white" : "scale-100 opacity-50 hover:opacity-75"
              )}>
              <img src={testimonial.photo} alt={testimonial.name} className="w-8 h-8 rounded-md object-cover" />
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

const TIERS = [
  {
    name: "Emerging",
    revenue: "Brands < $3m annual revenue",
    description: "Automate cash application and start winning repayments on deductions as far back as a year with the platform made for emerging brands like you.",
  },
  {
    name: "Growth",
    revenue: "Brands < $20m annual revenue",
    price: "$20",
    description: "Grow on your own terms, with clear visibility into your trade spend and deductions. Automate your toughest deductions and accounting workflows so you can focus on scaling your business.",
  },
  {
    name: "Scale",
    revenue: "Brands $20m–$50m annual revenue",
    description:
      "Accrue for your trade spend accurately and drill down into retailer level profitability.  Own your margin, grow on your terms, and supercharge your team.",
  },
  {
    name: "Enterprise",
    revenue: "Brands > $50m annual revenue",
    description: "Every payment, shipment, and promotion in one place, with AI insights and automation for your entire business.",
  },
]

export function PricingTiers() {
  const [selectedTier, setSelectedTier] = useState(0)

  return (
    <div class="space-y-4 mt-8">
      {TIERS.map((tier, index) => (
        <div key={tier.name} class={classes(index < TIERS.length - 1 && "border-b border-gray-400")}>
          <div class="p-4 flex justify-between items-center cursor-pointer select-none" onClick={() => setSelectedTier(index)}>
            <div>
              <div class="font-display text-xl">{tier.name}</div>
              <div class="text-gray-600">{tier.revenue}</div>
            </div>
            <div class="w-6">
              {selectedTier === index ? <MinusCircle className="text-indigo" /> : <PlusCircle className="text-indigo" />}
            </div>
          </div>

          {/* Expandable content */}
          <div
            class={classes(
              "transition-all duration-300 overflow-hidden",
              selectedTier === index ? "max-h-96" : "max-h-0"
            )}>
            <p class="text-gray-700 mb-4 mx-4">{tier.description}</p>
          </div>
        </div>
      ))}
    </div>
  )
}

export function Pricing() {
  return (
    <div id="pricing" class="bg-sky-100 py-16">
      <div class="max-w-6xl mx-auto flex gap-8 px-4">
        <div
          class="bg-cover bg-center bg-no-repeat rounded-4xl basis-1/2 hidden lg:flex items-center justify-center"
          style={{ backgroundImage: 'url("/img/landing/chips_background.webp")' }}>
          <img src="/img/landing/chips.webp" class="w-2/3 my-12 " />
        </div>
        <div class="basis-full lg:basis-1/2 lg:px-8 py-4">
          <div class="uppercase text-sm font-bold tracking-wider">Pricing</div>
          <div class="text-4xl lg:text-5xl font-display mt-4">
            Priced to guarantee ROI for all brands.
          </div>
          <div class="my-6">
            Our pricing is based on volume of deductions managed so that you get the kind of ROI you can talk about in your board meetings, no matter where you are in your growth journey.
          </div>
          <DemoLink class="px-4 py-2 text-white border border-indigo bg-indigo rounded-full uppercase text-sm font-bold hover:bg-indigo-600 hover:border-indigo-600 transition-colors" />
          <PricingTiers />
        </div>
      </div>
    </div>
  )
}

const BIOS = [
  {
    color: "bg-salmon",
    name: "Joey Caffrey",
    role: "CEO",
    picture: "/img/team/joey.webp",
    linkedin: "https://www.linkedin.com/in/joey-caffrey-58155687/",
    logos: [],
    summary: `Joey's been in CPG since birth because his parents worked in CPG analytics & operations (Nielsen, Advantage) for their entire careers. He studied Computer Science at Harvard and scaled startups in fintech, digital health before he dove deep into CPG immediately after his first Expo West.`,
  },
  {
    color: "bg-lime-200",
    name: "Nat Noone",
    role: "CRO",
    picture: "/img/team/nat.webp",
    linkedin: "https://www.linkedin.com/in/nat-noone-3211997/",
    logos: [],
    summary: `Nat brings over 30 years of CPG experience from every angle.
      He grew up on a truck with a juice company that was bought by Odwalla (later bought by Coca-Cola where he held several leadership positions).
      Most recently, he founded a national refrigerated DSD company and Wave Soda. He's thrilled to deliver the ultimate solution to some of the toughest problems he's felt first-hand in various roles over his career.`,
  },
]

export function Team() {
  return (
    <div id="team" class="pt-16 bg-orange-100">
      <SectionEyebrow>Company</SectionEyebrow>
      <SectionTitle>Founding Wizards</SectionTitle>
      {/* lg:w-lg (not max-w-*): the .landing-container > div > div rule forces max-w-6xl, so only width constrains here. */}
      <div class="mt-6 lg:w-lg mx-auto text-center px-4">
        We have a mix of CPG and tech experience, but brands choose us because we obsess over helping them grow their business and margin.
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 px-4 max-w-4xl mx-auto mt-12">
        {BIOS.map((member, index) => (
          <div class={classes(member.color, "rounded-4xl p-8 flex flex-col")}>
            <div class="flex justify-between">
              <div>
                <div class="font-display text-3xl">{member.name}</div>
                <div>{member.role}</div>
              </div>
              <img
                src={member.picture}
                alt={member.name}
                width={64}
                height={64}
                className="h-16 w-16 shrink-0 object-cover rounded-full bg-white"
              />
            </div>
            <div class="mt-6 grow">{member.summary}</div>
            <a
              href={member.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              class="mt-4"
              aria-label={`${member.name} on LinkedIn`}>
              <img src="/img/team/linkedin.webp" alt="" width={32} height={32} className="w-8 h-8 bg-white rounded-sm" />
            </a>
          </div>
        ))}
      </div>

    </div>
  )
}

export function FinalCta() {
  return (
    <div class="bg-orange-100 py-16 px-4">
      <div class="bg-[#5F5FFA] rounded-[50px] max-w-6xl mx-auto px-8 py-16 text-center">
        <div class="font-display text-4xl lg:text-5xl text-white">Better margins are brewing.</div>
        <div class="text-indigo-100 text-lg mt-4 max-w-xl mx-auto">
          See the wizard magic for yourself.
        </div>
        <DemoLink class="inline-block mt-8 px-6 py-3 bg-white rounded-full text-indigo-900 uppercase text-sm font-bold hover:bg-indigo-50 transition-colors" />
      </div>
    </div>
  )
}
